'use client';

import { proxy } from 'valtio';
import { Base64 } from 'js-base64';
import JSZip from 'jszip';
import { unzipRaw } from 'unzipit';
import tarball from './tarball';

export class Progress {
    constructor(key) {
        this.loading = false;
        this.percent = 0;
        this.key = key;
    }

    setLoading(value) {
        this.loading = value;
    }

    setPercent(percent) {
        this.percent = percent;
    }
}

export const baseProgress = proxy(new Progress('well_base'));
export const coordProgress = proxy(new Progress('well_coord'));
export const gtmProgress = proxy(new Progress('well_gtm'));
export const transitProgress = proxy(new Progress('well_transit_v3'));
export const stateProgress = proxy(new Progress('well_state'));
export const debitProgress = proxy(new Progress('well_debit'));
export const voronoiProgress = proxy(new Progress('voronoi'));

export class Cmd {
    constructor() {
        this.exec_command = window.module.cwrap('exec_command', null, ['string']);
        this.start_command = window.module.cwrap('start_command', null, ['string']);
        this.append_command_data = window.module.cwrap('append_command_data', null, ['string']);
        this.end_command = window.module.cwrap('end_command');
        this.get_data = window.module.cwrap('get_data', ['string'], ['string']);
    }

    getByteLength(string) {
        const encoder = new TextEncoder();
        const view = encoder.encode(string);
        return String(view.length);
    }

    getPayload(payload) {
        // Needs verification because the data might be false for this commands.
        if (
            payload.command === 'set_ruler_active' ||
            payload.command === 'set_lasso_active' ||
            payload.command === 'set_circle_active' ||
            payload.command === 'set_user_circles_active' ||
            payload.command === 'set_equidistant_active' ||
            payload.command === 'set_chain_active' ||
            payload.command === 'set_current_debit_type' ||
            payload.command === 'set_click_listener_active' ||
            payload.command === 'set_click_active'
        ) {
            return JSON.stringify({
                type: payload.command,
                data: payload.data,
            });
        }

        if (payload.data) {
            return JSON.stringify({
                type: payload.command,
                data: payload.data,
            });
        } else {
            return JSON.stringify({
                type: payload.command,
            });
        }
    }

    async getPayloadFromStreamCBOR(payload, isBase64 = false) {
        const stream = payload.data.body;

        if (isBase64) {
            const base64 = typeof stream === 'string' ? stream : await new Response(stream).text(); // читаем текст из ReadableStream
            return JSON.stringify({
                type: payload.command,
                data: base64,
            });
        } else {
            // HACK: v1
            const bufferPromise = new Response(stream).arrayBuffer();
            const buffer = await bufferPromise;
            const base64 = Base64.fromUint8Array(buffer);

            // HACK: v2
            // const bufferPromise = new Response(stream);
            // const blob = await bufferPromise.blob();
            // const url = URL.createObjectURL(blob);

            // HACK: v3
            // const textStream = stream.pipeThrough(new TextDecoderStream()).getReader();

            // const chunks = [];
            // while (true) {
            //     const { value, done } = await textStream.read();
            //     if (done) break;
            //     chunks.push(value);
            // }

            // const string = chunks.join(' ');

            // const base64 = Base64.encode(string);

            return JSON.stringify({
                type: payload.command,
                data: base64,
            });
        }
    }

    getPayloadFromFilesCBOR(payload) {
        const base64 = Base64.fromUint8Array(payload.data);

        return JSON.stringify({
            type: payload.command,
            data: base64,
        });
    }

    chunkString(str, len) {
        const size = Math.ceil(str.length / len);
        const r = Array(size);
        let offset = 0;

        for (let i = 0; i < size; i++) {
            r[i] = str.substr(offset, len);
            offset += len;
        }

        return r;
    }

    runBatchLoading(payload, chunkSize = 100000) {
        const byteLength = this.getByteLength(payload);
        this.start_command(byteLength);

        const chunks = this.chunkString(payload, chunkSize); // 100000 = 100kB

        for (let i = 0; i < chunks.length; i += 1) {
            this.append_command_data(chunks[i]);
        }

        this.end_command();
    }

    // 'time_interval',
    // 'unique_gtm_list',
    static async get(command) {
        await waitForModule();
        const cmd = new Cmd();

        return window.module.UTF8ToString(cmd.get_data(command));
    }

    /* command - object with "type" field */
    static async get_v2(command) {
        await waitForModule();
        const cmd = new Cmd();
        return window.module.UTF8ToString(cmd.get_data(JSON.stringify(command)));
    }

    // 'parse_field_wells'
    static async parseJSON(command, data) {
        await waitForModule();
        const cmd = new Cmd();

        // TODO: wasm error!
        const payload = cmd.getPayload({ command, data });
        // console.log(payload, command);

        cmd.runBatchLoading(payload);
    }

    /*
        'parse_map_production_cbor',
        'parse_well_gtm_cbor',
        'parse_well_transit_cbor',
        'parse_well_state_cbor',
    */
    static async parseStreamCBOR(command, data, isBase64 = false) {
        // const tStart = performance.now();
        await waitForModule();

        const cmd = new Cmd();
        // console.log(`tStart ${((performance.now() - tStart) / 1000).toFixed(3)} сек`);

        // const tMakePayload = performance.now();

        const payload = await cmd.getPayloadFromStreamCBOR({ command, data }, isBase64);
        // console.log(`tMakePayload ${((performance.now() - tMakePayload) / 1000).toFixed(3)} сек`);

        // const tLoad = performance.now();
        cmd.runBatchLoading(payload);
        // console.log(`tLoad ${((performance.now() - tLoad) / 1000).toFixed(3)} сек`);
    }

    static async parseFilesCBOR(command, data) {
        await waitForModule();
        const cmd = new Cmd();

        const payload = cmd.getPayloadFromFilesCBOR({ command, data });

        cmd.runBatchLoading(payload);
    }

    static async splitCBOR_v2(response) {
        const stream = response.body;
        const buffer = new Response(stream);
        const blob = await buffer.blob();

        const cborText = await blob.text();

        const parts = cborText.split('\n---\n');

        const filteredParts = parts.filter(item => item.length > 0);

        const files = [];
        for (let i = 0; i < filteredParts.length; ++i) {
            // if (i === 2) {
            //     // const text = await filteredParts[i].text();
            //     const blob = new Blob([filteredParts[2]], { type: 'application/cbor' });
            //     const url = URL.createObjectURL(blob);
            //     const downloadLink = document.createElement('a');
            //     downloadLink.href = url;
            //     downloadLink.download = `${'1'}.${'cbor'}`;
            //     downloadLink.click();
            //     return;
            // }

            files.push(Base64.toUint8Array(filteredParts[i]));
        }
        return files;
    }

    readAsBinaryString(file) {
        return new Promise((resolve, reject) => {
            const fr = new FileReader();
            fr.onerror = reject;
            fr.onload = () => {
                const parts = fr.result.split('\n---\n');

                const filteredParts = parts.filter(item => item.length > 0);

                const files = [];
                for (let i = 0; i < filteredParts.length; ++i) {
                    files.push(Base64.toUint8Array(filteredParts[i]));
                }
                resolve(files);
            };
            // fr.readAsBinaryString(file);
            fr.readAsText(file);
        });
    }

    static async splitCBOR(response) {
        await waitForModule();
        const cmd = new Cmd();

        const stream = response.body;
        const buffer = new Response(stream);
        const blob = await buffer.blob();

        const files = await cmd.readAsBinaryString(blob);
        return files;
    }

    static async unTar(response) {
        const stream = response.body;
        const buffer = new Response(stream);
        const blob = await buffer.blob();

        const tar = new tarball.TarReader();

        try {
            const parts = await tar.readFile(blob);

            const files = [];
            for (let i = 0; i < parts.length; ++i) {
                if (parts[i].type == 'file') {
                    files.push(tar.getFileBinary(parts[i].name));
                }
            }

            return files;
        } catch (error) {
            console.error('Cannot read tar');
        }
    }

    // TODO: данная реализация некорректно работает в браузере Microsoft Edge версии 109.
    // static async unZIP(data) {
    //     // const contentLength = +data.headers.get('Content-Length');

    //     const stream = data.body;
    //     const buffer = new Response(stream);
    //     const blob = await buffer.blob();
    //     const unzip = await JSZip.loadAsync(blob);

    //     const files = Object.values(unzip.files);

    //     // if (data.bodyUsed) {
    //     //     progress.setPercent('fetchProductionZIP', 100);
    //     //     progress.setLoading(false);
    //     // }

    //     return files;
    // }

    static async unZIP(data) {
        try {
            // const stream = data.body;
            // console.log(' stream', [stream]);
            // const buffer = new Response(stream);
            // console.log(' buffer', [buffer]);
            // const blob = await buffer.blob();
            // console.log('blob', [blob]);
            const stream = data.body; // Extract the ReadableStream
            console.log('stream', [stream]);

            // Read the stream fully into an ArrayBuffer
            const reader = stream.getReader();
            const chunks = [];
            let done = false;
            while (!done) {
                const { value, done: doneReading } = await reader.read();
                if (value) chunks.push(value);
                done = doneReading;
            }

            // Concatenate all chunks into a single Uint8Array
            const fullArray = new Uint8Array(chunks.reduce((acc, curr) => acc + curr.length, 0));
            let offset = 0;
            for (const chunk of chunks) {
                fullArray.set(chunk, offset);
                offset += chunk.length;
            }

            // Convert Uint8Array to Blob
            const blob = new Blob([fullArray]);
            console.log('blob', [blob]);

            // Use JSZip to load and unzip the blob
            const unzip = await JSZip.loadAsync(blob);
            console.log('unZIP', [unzip]);
            // Extract the files from the unzipped object
            const files = Object.values(unzip.files);
            console.log('unZIP', [files, blob, data, stream]);
            // Log and return the files for further use
            return files;
        } catch (error) {
            console.error('Error unzipping the stream: ', error);
            throw error;
        }
    }

    // TODO: данная реализация некорректно работает в браузере Microsoft Edge версии 109.
    static async unZIP_v2(data) {
        const stream = data.body;
        const buffer = new Response(stream);
        const blob = await buffer.blob();

        const unziped = await unzipRaw(blob);

        // let files = [];
        // raw.entries.forEach(async item => {
        //     const arrayBuffer = await item.arrayBuffer();
        //     files.push(new Uint8Array(arrayBuffer));

        //     // const reader = new FileReader();
        //     // const blob = await item.blob(); // 'application/cbor'
        //     // reader.readAsArrayBuffer(blob);
        //     // reader.onload = async () => {
        //     //     await files.push(new Uint8Array(reader.result));
        //     // };
        // });

        return unziped.entries.map(async item => {
            const arrayBuffer = await item.arrayBuffer();
            return new Uint8Array(arrayBuffer);
        });
    }

    /*
        'update_plot',
        'set_current_time',
        'set_ruler_active',
        'set_lasso_active',
        'set_circle_active',
        'create_layer',
        'set_layer_visible',
        'set_layer_settings',
        'remove_layer',
        'set_current_debit_type',
        'set_scale',
        'set_focused_well',
    */
    static async exec(command, data = null) {
        await waitForModule();
        const cmd = new Cmd();

        const payload = cmd.getPayload({ command, data });

        cmd.exec_command(payload);
    }

    /*
        HACK: simple way for download large cbor/zip/json files.

        Stream not be locked!

        Examples:
        const rawWellsZIP = await api.mapGeneration.fetchProductionZIP_v2({
            stratums: state.stratums,
            field: state.field,
            versions: state.versions,
            cbor: true,
            chunkSize: 10
        });
        await Cmd.download(rawWellsZIP, 'rawWellsZIP', 'application/zip');
    */
    static async download(response, filename = new Date().toLocaleDateString(), MEMEtype = 'application/cbor') {
        const stream = response.body;
        const buffer = new Response(stream);
        const blob = await buffer.blob();

        // TODO: to extend the implementation.
        // const text = await buffer.text();
        // const blob = new Blob([text], { type: MEMEtype });

        const url = URL.createObjectURL(blob);
        const downloadLink = document.createElement('a');
        downloadLink.href = url;

        let fileExtension;

        switch (MEMEtype) {
            case 'application/zip':
                fileExtension = 'zip';
                break;

            case 'application/cbor':
                fileExtension = 'cbor';
                break;

            // TODO: not have implementation.
            // case 'application/json':
            //     fileExtension = 'json';
            //     break;

            default:
                fileExtension = 'cbor';
                break;
        }

        downloadLink.download = `${filename}.${fileExtension}`;
        downloadLink.click();
    }
}

export async function waitForModule(retries = 20, delay = 100) {
    while (!window.module || typeof window.module.cwrap !== 'function') {
        if (--retries <= 0) throw new Error('WASM module did not initialize in time.');
        await new Promise(res => setTimeout(res, delay));
    }
}
